/**
 * PageDiscussion — Comment thread component for page discussions.
 *
 * Displays comments in chronological order with author name, relative timestamp,
 * and content. Includes an input to add new comments with @ mention support.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as pagesApi from '../../api/pages-api';
import { Button } from '../../components/ui/button';
import { MarkdownText } from '../../components/MarkdownText';

interface PageDiscussionProps {
  readonly pageId: string;
}

interface MentionableUser {
  readonly userId: string;
  readonly displayName: string;
}

export function PageDiscussion({ pageId }: PageDiscussionProps): React.ReactElement {
  const [comments, setComments] = useState<pagesApi.PageComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Mentionable users (fetched from the users endpoint)
  const [mentionableUsers, setMentionableUsers] = useState<MentionableUser[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionTriggerPos, setMentionTriggerPos] = useState(-1);

  // Fetch users for @ mentions
  useEffect(() => {
    let cancelled = false;
    async function fetchUsers(): Promise<void> {
      try {
        const result = await pagesApi.listUsers();
        if (!cancelled) {
          setMentionableUsers(
            (result.data ?? []).map((u) => ({ userId: u.userId, displayName: u.displayName }))
          );
        }
      } catch {
        // Non-fatal — mentions just won't autocomplete
      }
    }
    fetchUsers();
    return () => { cancelled = true; };
  }, []);

  // Mention filtering
  const filteredMentions = useCallback((): MentionableUser[] => {
    if (mentionQuery === null) return [];
    if (mentionQuery === '') return mentionableUsers.slice(0, 8);
    const q = mentionQuery.toLowerCase();
    return mentionableUsers.filter((u) => u.displayName.toLowerCase().includes(q)).slice(0, 8);
  }, [mentionableUsers, mentionQuery])();

  // Insert selected mention
  const insertMention = useCallback((user: MentionableUser) => {
    if (mentionTriggerPos < 0) return;
    const before = newComment.slice(0, mentionTriggerPos);
    const cursorPos = textareaRef.current?.selectionStart ?? newComment.length;
    const after = newComment.slice(cursorPos);
    const inserted = `@${user.displayName} `;
    setNewComment(before + inserted + after);
    setMentionQuery(null);
    setMentionTriggerPos(-1);

    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = (before + inserted).length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  }, [mentionTriggerPos, newComment]);

  // Detect @ trigger in textarea
  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNewComment(value);

    if (mentionableUsers.length === 0) return;

    const cursorPos = e.target.selectionStart ?? value.length;
    const textBeforeCursor = value.slice(0, cursorPos);

    let atPos = -1;
    for (let i = textBeforeCursor.length - 1; i >= 0; i--) {
      if (textBeforeCursor[i] === '@') {
        if (i === 0 || /[\s\n]/.test(textBeforeCursor[i - 1])) {
          atPos = i;
          break;
        }
      }
    }

    if (atPos >= 0) {
      const query = textBeforeCursor.slice(atPos + 1);
      if (query.length > 30 || query.includes('\n')) {
        setMentionQuery(null);
        setMentionTriggerPos(-1);
      } else {
        setMentionQuery(query);
        setMentionTriggerPos(atPos);
        setMentionIndex(0);
      }
    } else {
      setMentionQuery(null);
      setMentionTriggerPos(-1);
    }
  }, [mentionableUsers]);

  // Keyboard handling for mention dropdown
  const handleMentionKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery === null || filteredMentions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex((prev) => Math.min(prev + 1, filteredMentions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (filteredMentions[mentionIndex]) {
        e.preventDefault();
        insertMention(filteredMentions[mentionIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMentionQuery(null);
      setMentionTriggerPos(-1);
    }
  }, [mentionQuery, filteredMentions, mentionIndex, insertMention]);

  // Fetch comments on mount and when pageId changes
  useEffect(() => {
    let cancelled = false;

    async function fetchComments(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const response = await pagesApi.listPageComments(pageId);
        if (!cancelled) {
          setComments(response.data ?? []);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const apiErr = err as { statusCode?: number; message?: string };
          if (apiErr.statusCode === 404) {
            setError('Comments are not yet available for this page.');
          } else {
            setError(apiErr.message ?? 'Failed to load comments.');
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchComments();
    return () => { cancelled = true; };
  }, [pageId]);

  // Scroll to bottom when new comments are added
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments.length]);

  // Add a new comment
  const handleSubmit = useCallback(async () => {
    const trimmed = newComment.trim();
    if (!trimmed || submitting) return;

    setSubmitting(true);
    try {
      const comment = await pagesApi.addPageComment(pageId, { content: trimmed });
      setComments((prev) => [...prev, comment]);
      setNewComment('');
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message ?? 'Failed to add comment.');
    } finally {
      setSubmitting(false);
    }
  }, [pageId, newComment, submitting]);

  // Start editing a comment
  const startEdit = useCallback((comment: pagesApi.PageComment) => {
    setEditingId(comment.commentId);
    setEditContent(comment.content);
  }, []);

  // Save edited comment
  const saveEdit = useCallback(async () => {
    if (!editingId) return;
    const trimmed = editContent.trim();
    if (!trimmed) return;

    try {
      const updated = await pagesApi.editPageComment(pageId, editingId, { content: trimmed });
      setComments((prev) =>
        prev.map((c) => (c.commentId === editingId ? updated : c))
      );
      setEditingId(null);
      setEditContent('');
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message ?? 'Failed to edit comment.');
    }
  }, [pageId, editingId, editContent]);

  // Cancel editing
  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditContent('');
  }, []);

  return (
    <div className="flex flex-col h-full">
      <h3 className="text-sm font-semibold mb-3">Discussion</h3>

      {error && (
        <p className="text-xs text-muted-foreground mb-3 bg-muted/50 rounded px-2 py-1.5" role="alert">
          {error}
        </p>
      )}

      {/* Comments list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 min-h-0">
        {loading && (
          <p className="text-sm text-muted-foreground" role="status">Loading comments...</p>
        )}

        {!loading && comments.length === 0 && !error && (
          <p className="text-sm text-muted-foreground">No comments yet. Start the discussion!</p>
        )}

        {comments.map((comment) => (
          <div key={comment.commentId} className="border rounded-md p-2.5 text-sm">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-xs">{comment.authorName}</span>
              <div className="flex items-center gap-1.5">
                {comment.updatedAt && (
                  <span className="text-xs text-muted-foreground">(edited)</span>
                )}
                <span className="text-xs text-muted-foreground">
                  {formatRelativeTime(comment.createdAt)}
                </span>
              </div>
            </div>

            {editingId === comment.commentId ? (
              <div className="mt-1">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full text-sm border rounded px-2 py-1 bg-background resize-none min-h-[60px] focus:outline-none focus:ring-1 focus:ring-ring"
                  aria-label="Edit comment"
                />
                <div className="flex gap-1 mt-1">
                  <Button size="sm" variant="default" className="text-xs h-6" onClick={saveEdit}>
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" className="text-xs h-6" onClick={cancelEdit}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between">
                <div className="text-foreground whitespace-pre-wrap break-words"><MarkdownText text={comment.content} /></div>
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground ml-2 shrink-0"
                  onClick={() => startEdit(comment)}
                  title="Edit comment"
                  aria-label="Edit comment"
                >
                  ✎
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* New comment input */}
      <div className="mt-3 border-t pt-3 relative">
        <textarea
          ref={textareaRef}
          value={newComment}
          onChange={handleCommentChange}
          placeholder="Add a comment... (type @ to mention someone)"
          className="w-full text-sm border rounded px-2.5 py-2 bg-background resize-none min-h-[64px] focus:outline-none focus:ring-1 focus:ring-ring"
          aria-label="New comment"
          onKeyDown={(e) => {
            // Handle mention navigation first
            if (mentionQuery !== null && filteredMentions.length > 0) {
              handleMentionKeyDown(e);
              if (e.defaultPrevented) return;
            }
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />

        {/* Mention autocomplete dropdown */}
        {mentionQuery !== null && filteredMentions.length > 0 && (
          <div
            className="absolute bottom-full mb-1 left-0 w-64 max-h-48 overflow-y-auto rounded-md border bg-popover shadow-lg z-50"
            role="listbox"
            aria-label="Mention suggestions"
          >
            {filteredMentions.map((user, index) => (
              <button
                key={user.userId}
                type="button"
                role="option"
                aria-selected={index === mentionIndex}
                className={`w-full text-left px-3 py-2 text-sm cursor-pointer transition-colors
                  ${index === mentionIndex ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}
                `}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertMention(user);
                }}
                onMouseEnter={() => setMentionIndex(index)}
              >
                <span className="truncate block">@{user.displayName}</span>
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mt-1.5">
          <span className="text-xs text-muted-foreground">Ctrl+Enter to send</span>
          <Button
            size="sm"
            variant="default"
            className="text-xs h-7"
            disabled={!newComment.trim() || submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Sending...' : 'Add comment'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Format a timestamp as a relative time string (e.g., "2 min ago").
 */
function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
